"use client";

/**
 * useNavDisclosure — shared open/close state machine for the navbar's hover menus.
 *
 * Both {@link ../_components/canvas/renderers/_shared/NavDropdown} (a single nav group) and
 * {@link ../_components/canvas/renderers/_shared/NavOverflowMenu} (the priority+ "More" tail) need
 * the exact same disclosure behavior: open on hover, close after a short delay when the pointer
 * leaves, toggle on tap for coarse-pointer (touch) devices, and dismiss on an outside pointer-down.
 * Keeping that logic here means a fix lands once instead of drifting across two copies.
 *
 * The hover-vs-tap decision reads `(hover: hover)` once and caches it for the component's lifetime
 * (rather than re-querying `matchMedia` on every tap).
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { NAV_DROPDOWN_CLOSE_DELAY_MS } from "../_logic/constants";

interface NavDisclosure {
  /** Whether the panel is currently open. */
  isOpen: boolean;
  /** Attach to the relative wrapper that contains both the trigger and the panel. */
  containerRef: React.RefObject<HTMLDivElement>;
  /** Spread onto the wrapper — opens on enter, schedules a delayed close on leave. */
  containerProps: { onMouseEnter: () => void; onMouseLeave: () => void };
  /** Spread onto the trigger `<button>` — taps toggle on touch devices, no-op on hover devices. */
  triggerProps: { onClick: () => void; "aria-expanded": boolean };
  /** Imperatively close the panel (e.g. after following an anchor link). */
  close: () => void;
}

/**
 * Provide the navbar hover-menu disclosure behavior (open/close, hover delay, tap toggle, outside
 * dismiss) shared by the dropdown and the overflow menu.
 *
 * @returns the disclosure state plus the props/ref the trigger and wrapper must attach.
 */
export function useNavDisclosure(): NavDisclosure {
  // States
  const [isOpen, setIsOpen] = useState(false);

  // Refs
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hoverCapableRef = useRef<boolean | null>(null);

  // Handlers
  const open = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(true);
  }, []);

  const close = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen(false);
  }, []);

  const scheduleClose = useCallback(() => {
    timeoutRef.current = setTimeout(() => setIsOpen(false), NAV_DROPDOWN_CLOSE_DELAY_MS);
  }, []);

  const handleToggle = useCallback(() => {
    // Only handle taps on coarse-pointer devices — desktop uses hover to open/close. Resolve the
    // media query once and cache it rather than re-querying on every tap.
    if (hoverCapableRef.current === null) {
      hoverCapableRef.current = typeof window !== "undefined" && window.matchMedia("(hover: hover)").matches;
    }
    if (hoverCapableRef.current) return;
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    setIsOpen((current) => !current);
  }, []);

  // Effects
  useEffect(() => {
    if (!isOpen) return;
    const handleOutsidePointerDown = (e: PointerEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setIsOpen(false);
    };
    document.addEventListener("pointerdown", handleOutsidePointerDown);
    return () => document.removeEventListener("pointerdown", handleOutsidePointerDown);
  }, [isOpen]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  return {
    isOpen,
    containerRef,
    containerProps: { onMouseEnter: open, onMouseLeave: scheduleClose },
    triggerProps: { onClick: handleToggle, "aria-expanded": isOpen },
    close,
  };
}
